<template>
  <EHSModal
    :headerTitle="$t('Button.additionalinformation')"
    :primaryButtonText="isDirty ? $t('Button.Save') : $t('Button.OK')"
    :primaryActionFunction="primaryActionFunction"
    :secondaryButtonText="$t('Button.Discard')"
    :secondaryActionFunction="onModalClose"
    :hasSecondaryAction="isDirty"
    class="ehs-modal"
  >
    <Layout column center vld-parent>
      <Loading
        :active.sync="loading"
        :canCancel="false"
        :isFullPage="false"
        backgroundColor="#f0f0f0"
        loader="bars"
      />
      <Layout column class="w-full" :shrink="false" v-if="inputComponentName !== 'TextInput'">
        <Flex pl-4 font-medium self-start>
          {{ $t('vue.common.labels.previousInputs') }}
        </Flex>
        <DataTable
          :columns="historicTableColumns"
          :data="historicTableData"
          :pagination="false"
          :wrap="true"
          class="mb-5"
        />
      </Layout>
      <Layout column class="w-full">
        <Flex pl-4 font-medium self-start>
          {{ $t('vue.common.labels.comments') }}
        </Flex>
        <DataTable
          :columns="commentsColumns"
          :data="commentsData"
          :pagination="false"
          :wrap="true"
          class="comments-table"
        />
      </Layout>
      <Layout center self-start pl-4 class="w-3/4" :shrink="true">
        <FieldDetacher>
          <FormField name="commentInputField" class="pl-0">
            <TextArea
              :placeholder="$t('addNewComment')"
              :value.sync="commentInput"
              :rules="[() => isCommentValid || $t('vue.common.labels.inputTooLong')]"
              :ref="'commentInput'"
              class="flex-grow"
              :style="{ 'max-height': '6rem' }"
              :disabled="addingCommentsAreDisabled"
            ></TextArea>
          </FormField>
        </FieldDetacher>
        <Button
          :disabled="
            !commentInput || commentInput.length > maxCommentLength || addingCommentsAreDisabled
          "
          prependIcon="bwise/action_add"
          class="ml-4"
          color="primary"
          @click="onCommentAddClick"
          >{{ $t('Button.Add') }}</Button
        >
      </Layout>
    </Layout>
  </EHSModal>
</template>

<script>
import Flex from '@wizer/core/components/flex/Flex'
import DataTable from '@wizer/core/components/data-table/DataTable'
import Layout from '@wizer/core/components/layout/Layout'
import TextArea from '@wizer/core/components/text-area/TextArea'
import Button from '@wizer/core/components/button/Button'
import FormField from '@wizer/core/components/form/FormField'
import Loading from 'vue-loading-overlay'
import _ from 'lodash'
import SustainabilityDataService from '@/services/SustainabilityDataService'
import NumberFormatMixin from '@/mixins/NumberFormatMixin'
import EHSModal from '@/components/EHSModal'
import FieldDetacher from '@/components/common/FieldDetacher'

export default {
  name: 'AdditionalInformationDialog',
  components: {
    FieldDetacher,
    Loading,
    EHSModal,
    DataTable,
    Layout,
    TextArea,
    Button,
    FormField,
    Flex,
  },
  mixins: [NumberFormatMixin],
  props: {
    item: Object,
    currentInputValue: [Number, String],
    outstandingComments: Array,
    previouslySaved: Boolean,
    parentId: String,
    inputComponentName: String,
    contextValidationMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      historicTableColumns: [],
      historicTableData: [],
      commentsColumns: [
        { property: 'comment', label: this.$t('vue.common.labels.comment'), width: '2/3' },
        { property: 'dateTime', label: this.$t('vue.common.labels.date') },
        { property: 'person', label: this.$t('vue.common.labels.enteredBy') },
      ],
      commentInput: '',
      commentsData: [],
      addedComments: [],
      isDirty: false,
      maxCommentLength: 2000,
    }
  },
  async mounted() {
    await this.getPreviousGridData()
    await this.getTileComments()
    this.prepareExplanationInput()
  },
  computed: {
    isCommentValid() {
      if (this.contextValidationMessage && !this.commentInput && !this.commentsData.length) {
        return this.contextValidationMessage
      }
      return this.commentInput.length <= this.maxCommentLength
    },
    primaryActionFunction() {
      if (!this.previouslySaved && this.isDirty) {
        return this.persistCommentsForOutstandingTile
      }
      if (this.isDirty) {
        return this.postAddedComments
      }
      return this.onModalClose
    },
    addingCommentsAreDisabled() {
      return (
        !this.item.resultExists &&
        !this.previouslySaved &&
        !this.currentInputValue &&
        !this.currentInputValue === 0
      )
    },
  },
  watch: {
    contextValidationMessage() {
      this.$refs.commentInput.validate()
    },
  },
  methods: {
    prepareExplanationInput() {
      if (this.contextValidationMessage) {
        this.$refs.commentInput.validate()
        this.$refs.commentInput.inputFocus()
        this.$refs.commentInput.$el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    async persistCommentsForOutstandingTile() {
      this.$emit('outstandingTileCommentsAdded', this.commentsData)
      this.onModalClose()
    },
    async postAddedComments() {
      this.loading = true
      let commentsSavedSuccessfully = true
      try {
        await SustainabilityDataService.postAddedComments(this.addedComments, this.parentId)
      } catch (error) {
        const message = error.response ? error.response.data : error
        const notification = { type: 'error', message }
        this.$store.dispatch('Notification/add', notification, { root: true })
        commentsSavedSuccessfully = false
      } finally {
        if (commentsSavedSuccessfully) {
          this.$emit('commentsUpdated')
        }
        this.loading = false
        this.onModalClose()
      }
    },
    onCommentAddClick() {
      if (this.commentInput) {
        const comment = {
          comment: this.commentInput,
          dateTime: new Date().toLocaleString(this.$profile.locale),
          person: this.$profile.name,
          personId: this.$profile.id,
        }
        this.commentsData.push(_.cloneDeep(comment))
        this.addedComments.push(_.cloneDeep(comment))
        this.commentInput = ''
        this.$refs.commentInput.inputValue = ''
        this.isDirty = true
      }
    },
    async getTileComments() {
      if (!this.previouslySaved) {
        this.commentsData = _.cloneDeep(this.outstandingComments)
      } else {
        const tileComments = (await SustainabilityDataService.getExistingComments(this.parentId))
          .data
        const sortedTileComments = tileComments.items.sort(
          (a, b) => new Date(b.dateTime) - new Date(a.dateTime),
        )
        this.commentsData = sortedTileComments.map((item) => ({
          comment: item.comment,
          person: item.person,
          dateTime: new Date(Date.parse(item.dateTime)).toLocaleString(this.$profile.locale),
        }))
      }
    },
    async getPreviousGridData() {
      const previousGridData = (await SustainabilityDataService.getPreviousGridData(this.item)).data
      this.historicTableColumns = previousGridData.columns.map((column) => ({
        property: column.timePeriodId ? column.timePeriodId : column.field,
        label: column.label,
        sortable: false,
      }))

      const inputs = previousGridData.inputs[0]
      _.merge(inputs, inputs.timePeriodValues)
      delete inputs.timePeriodValues
      // assign the current value
      if (this.currentInputValue || this.currentInputValue === 0) {
        inputs[this.item.timePeriodId] = this.currentInputValue
      }
      this.historicTableData = [this.localizeHistoricValues(inputs)]

      this.loading = false
    },
    localizeHistoricValues(inputs) {
      const localizedInputValues = {}
      _.forOwn(inputs, (value, key) => {
        if (this.isDecimal(value)) {
          localizedInputValues[key] = this.getLocalizedInputValue(value.toString(), true)
        } else {
          // tile values for outstanding timePeriods are undefined
          localizedInputValues[key] = value?.toString()
        }
      })
      return localizedInputValues
    },
    async onModalClose() {
      this.$emit('close')
    },
  },
}
</script>
<!-- Can't be scoped as we are overriding a rule on a data table-->
<style lang="postcss">
/* Assuming that the comment input is the first column in the comments-table */
.comments-table .w-data-table-row .w-data-table-row-cell:first-child {
  @apply whitespace-pre-line;
}
</style>
